/**
 * The player's flashlight. Parented to the camera, so it needs no per-frame
 * placement — only its own decay.
 *
 * There is also a very weak always-on point light at the lens. It is not the
 * torch: it exists so that a level with every tube dead still reads as a room
 * you are standing in rather than a black screen. It is deliberately left out
 * of `lightLevelAt`, so the player can just about see while sanity still drains.
 * It is also deliberately left out of the light drain below, because it is the
 * thing standing between a drained corridor and a soft lock.
 */

import * as THREE from 'three';
import { TUNING } from '../core/config';
import { clamp01, damp, hash1, noise1, smoothstep } from '../core/math';

/** Converts TUNING.flashlight.intensity into candela for the physical falloff. */
const INTENSITY_SCALE = 2.9;
/** Softer than inverse-square: a torch beam should carry down a corridor. */
const BEAM_DECAY = 1.35;

const HEALTHY = new THREE.Color(0xfff2dc);
const DYING = new THREE.Color(0xff9838);

export interface Flashlight {
  /** Parent this to the camera. */
  readonly root: THREE.Object3D;
  /** Beam strength 0..1 including brown-out and stutter. */
  readonly strength: number;
  readonly range: number;
  /** Cosine of the cone half-angle, for cheap "is this point lit" tests. */
  readonly cosHalfAngle: number;
  set(on: boolean, charge: number): void;
  /**
   * 0..1 light drain where the player is standing. The torch browns out and
   * stutters inside it — the one thing the player trusts failing — but only
   * ever partway, and it comes straight back when the drain lets up.
   */
  setDrain(v: number): void;
  update(dt: number, time: number): void;
  dispose(): void;
}

export function makeFlashlight(): Flashlight {
  const t = TUNING.flashlight;
  const root = new THREE.Object3D();
  root.matrixAutoUpdate = false;
  root.updateMatrix();

  const beam = new THREE.SpotLight(HEALTHY.getHex(), 0, t.range, t.coneAngle, 0.45, BEAM_DECAY);
  // Held slightly right of and below the eye, so the cone edge crawls over
  // doorframes as the player turns instead of being welded to the crosshair.
  beam.position.set(0.16, -0.1, 0);
  beam.castShadow = false;

  const target = new THREE.Object3D();
  target.position.set(0.12, -0.06, -1);
  beam.target = target;

  const lens = new THREE.PointLight(0xfff0dc, 0, 4.4, 1.6);
  lens.position.set(0, -0.05, -0.1);
  lens.castShadow = false;

  root.add(beam, target, lens);

  let on = false;
  let charge = 100;
  let fade = 0;
  let strength = 0;
  let drainTarget = 0;
  let drain = 0;

  return {
    root,
    range: t.range,
    cosHalfAngle: Math.cos(t.coneAngle),
    get strength(): number {
      return strength;
    },

    set(nextOn: boolean, nextCharge: number): void {
      on = nextOn;
      charge = nextCharge;
    },

    setDrain(v: number): void {
      drainTarget = clamp01(v);
    },

    update(dt: number, time: number): void {
      // A short fade so toggling does not click the whole room on and off.
      fade = damp(fade, on ? 1 : 0, 26, dt);
      // Lagged both ways. Walking into the dark is a sag rather than a switch,
      // and — the part that matters — a drainer that dies or despawns hands the
      // beam back over about a second instead of snapping it on.
      drain = damp(drain, drainTarget, 2.2, dt);

      const dying = on ? 1 - smoothstep(0, t.dyingThreshold, charge) : 0;
      // One failure envelope, two causes. A flat cell and a thing standing in
      // the corridor should sound and look like the same fault, because to the
      // player they are: the torch is going.
      const fail = Math.max(dying, drain);
      let s = fade * (1 - 0.68 * dying) * (1 - drain * TUNING.render.lightDrainTorchDepth);
      if (fail > 0.12) {
        // A failing cell sags and cuts, it does not pulse.
        const cut = hash1(Math.floor(time * 6.5), 0x51ed) < 0.14 * fail ? 0.1 : 1;
        s *= cut * (1 - 0.22 * fail * noise1(time * 5.3, 0x2b17));
      }
      strength = clamp01(s);

      beam.intensity = t.intensity * INTENSITY_SCALE * strength;
      beam.color.copy(HEALTHY).lerp(DYING, clamp01(Math.max(dying, drain * 0.85)));
      lens.intensity = 0.42 + 0.5 * fade * (1 - 0.5 * dying);
    },

    dispose(): void {
      root.parent?.remove(root);
      root.clear();
      beam.dispose();
      lens.dispose();
    },
  };
}
